import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useTheme } from '../../../../../theming/useTheme';
import { VrLineHeight } from '../../../../../theming/verticalRhythm/verticalRhythm';
import { footerSectionPadding } from '../footer.utils';
import { TextLinkComponent } from '../../../../partials/link/TextLink';
import { MEDIA_BREAKPOINTS } from '../../../../../mediaQueries.const';

const componentConfigurationKey = 'footerSocialLinks';

interface SocialLink {
  href: string
  src: string
  alt: string
}
export interface SocialSectionDataProps {
  title: string
  imageLinks: SocialLink[]
}

interface SocialLinksThemeProps {
  vrLineHeight: VrLineHeight
  titleColor: string
  circleColor: string
}

const getSocialLinksStyle = (vrLineHeight: VrLineHeight, titleColor: string, circleColor: string, numberOfColumns: number) => {
  const padding = footerSectionPadding(vrLineHeight);

  return StyleSheet.create({
    section: {
      ...padding,
    },
    title: {
      textAlign: 'center',
      color: titleColor,
    },
    storesWrapper: {
      display: 'grid',
      gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`,
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridGap: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
      },
    },
    circle: {
      width: '40px',
      height: '40px',
      backgroundColor: circleColor,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
};

const createStoreItem = (circleStyle: object) => (socialLink: SocialLink, index: number) => {
  return (
    <TextLinkComponent href={socialLink.href} key={index} rel="noopener" target="_blank">
      <div className={css(circleStyle)}>
        <img alt={socialLink.alt} src={socialLink.src} height="16" />
      </div>
    </TextLinkComponent>
  );
};

const maxSocialItemsPerRow = 5;

export const FooterSocialLinksComponent = (props: SocialLinksThemeProps & SocialSectionDataProps) => {
  const { title, imageLinks, vrLineHeight, titleColor, circleColor } = props;
  const style = getSocialLinksStyle(vrLineHeight, titleColor, circleColor, Math.min(imageLinks.length, maxSocialItemsPerRow));
  return (
    <div className={css(style.section)}>
      <h4 className={css(style.title)}>{title}</h4>
      <div className={css(style.storesWrapper)}>
        {imageLinks.map(createStoreItem(style.circle))}
      </div>
    </div>
  );
};

const mapThemeProps = (theme: SocialLinksThemeProps) => ({
  vrLineHeight: theme.vrLineHeight,
  titleColor: theme.titleColor,
  circleColor: theme.circleColor,
});

export const FooterSocialLinks = (props: SocialSectionDataProps) => {
  const themeContext = useTheme(componentConfigurationKey, mapThemeProps);
  return <FooterSocialLinksComponent {...themeContext} {...props} />;
};
